"use client";

import { useEffect, useState } from "react";

type Theme = "light" | "dark";

const THEME_STORAGE_KEY = "theme";

/**
 * Gets the current theme preference.
 * Checks localStorage first, then falls back to OS preference.
 * Wraps localStorage access in try/catch for hydration safety.
 */
export function getTheme(): Theme {
  // Check localStorage with error handling (for SSR, incognito, etc.)
  try {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored === "light" || stored === "dark") {
        return stored;
      }
    }
  } catch (error) {
    // localStorage unavailable (incognito mode, disabled, etc.)
    // Fall through to default (light mode)
  }

  // Default to light mode (no longer checking OS preference)
  return "light";
}

/**
 * Sets the theme and updates the DOM.
 * Wraps localStorage access in try/catch for hydration safety.
 */
export function setTheme(theme: Theme): void {
  if (typeof window === "undefined") {
    return;
  }

  const root = document.documentElement;

  // Update localStorage with error handling
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch (error) {
    // localStorage unavailable (incognito mode, disabled, etc.)
    // Continue anyway to update the DOM
  }

  // Update DOM class
  if (theme === "dark") {
    root.classList.add("dark");
    root.setAttribute("data-mantine-color-scheme", "dark");
  } else {
    root.classList.remove("dark");
    root.setAttribute("data-mantine-color-scheme", "light");
  }
}

/**
 * Toggles between light and dark theme.
 */
export function toggleTheme(): Theme {
  const currentTheme = getTheme();
  const newTheme = currentTheme === "dark" ? "light" : "dark";
  setTheme(newTheme);
  return newTheme;
}

/**
 * React hook for managing theme state in client components.
 * Handles initial render with useEffect to avoid hydration mismatches.
 */
export function useTheme(): {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
} {
  // Initialize with "light" to avoid hydration mismatch
  // The actual theme will be set in useEffect
  const [theme, setThemeState] = useState<Theme>("light");
  const [mounted, setMounted] = useState(false);

  // Handle initial render and hydration safety
  useEffect(() => {
    setMounted(true);
    const currentTheme = getTheme();
    setThemeState(currentTheme);

    // Apply theme to DOM if not already set (defensive)
    if (currentTheme === "dark") {
      document.documentElement.classList.add("dark");
      document.documentElement.setAttribute("data-mantine-color-scheme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      document.documentElement.setAttribute("data-mantine-color-scheme", "light");
    }

    // Listen for OS preference changes (only if no localStorage preference)
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = () => {
      try {
        // Only react to OS changes if user hasn't set a preference
        // Default to light mode instead of following OS preference
        if (!localStorage.getItem(THEME_STORAGE_KEY)) {
          setThemeState("light");
          setTheme("light");
        }
      } catch (error) {
        // localStorage unavailable, ignore
      }
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  const handleSetTheme = (newTheme: Theme) => {
    setTheme(newTheme);
    setThemeState(newTheme);
  };

  const handleToggleTheme = () => {
    const newTheme = toggleTheme();
    setThemeState(newTheme);
    return newTheme;
  };

  // Return "light" during SSR to avoid hydration mismatch
  // Once mounted, return the actual theme
  return {
    theme: mounted ? theme : "light",
    setTheme: handleSetTheme,
    toggleTheme: handleToggleTheme,
  };
}
